import { Input } from 'antd';
import cx from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from 'components/ui/Button';
import { AppRoutes } from 'config/routes';
import { AsideFilter } from './filter/filter';

import { ReactComponent as FilterIcon } from 'assets/images/filter.svg';
import { ReactComponent as SearchIcon } from 'assets/images/search-dashboard.svg';
import { ReactComponent as PlusIcon } from 'assets/images/second-plus.svg';

import { Loader } from 'components/loader/loader';

import axios from 'axios';
import { Icons } from 'components/icons';
import { NotFound, NotFoundContainer } from 'components/not-found';
import { useAlert } from 'react-alert';
import { useGetMyOrganizationParticipationsQuery } from 'store/api/organizations';
import { OrganizationParticipation } from 'store/api/types';
import { useAppDispatch } from 'store/hooks';
import { resetFuelFormState } from 'store/reducers/contract.reducer';
import { useDebounce } from 'usehooks-ts';
import { getToken } from 'utils/token';
import { getAcceptLanguage } from 'utils/locale';
import s from './List.module.scss';
import { Table } from './Table';
import { AsideTableSettings } from './aside-table-settings/table-settings';
import { FiltersValues } from './filter/config';

export interface IList {
  title: string;
  filters: {
    type: string;
    field: {
      label: string;
      value: string;
    };
    options: {
      label: string;
      value: string;
    }[];
  }[];
  headers: {
    [key: string]: string;
  }[];
  records: {
    id: string;
    orgOwnerId: string;
    clickOperation: string;
    data: {
      [key: string]: string | number | boolean | null;
    };
  }[];
  options: {
    isCreatable: boolean;
    isTerminable: boolean;
  };
  meta: {
    limit: number;
    page: number;
    total: number;
  };
}

export const findApiUrlByFieldPath = (
  fieldPath: string,
  organizationsData: OrganizationParticipation[] | undefined,
): string | null => {
  for (const org of organizationsData || []) {
    for (const widget of org.application.widgets || []) {
      for (const field of widget.fields || []) {
        if (field.path === fieldPath) {
          return field.api;
        }
      }
    }
  }

  return null;
};

export const Label = ({ text }: { text: string }) => {
  const { t } = useTranslation();

  return (
    <div className={s['label-container']}>
      <span className={s['label-text']}>{t(text)}</span>
    </div>
  );
};

function List() {
  const alert = useAlert();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Home, CustomWidget } = AppRoutes.Dashboard;
  const [scheme, setScheme] = useState<IList>();
  const [isRevokeLoading, setIsRevokeLoading] = useState<boolean>(false);

  const { data: widgets } = useGetMyOrganizationParticipationsQuery();

  const urlParams = new URLSearchParams(window.location.search);
  const name = urlParams.get('name');

  const apiUrl = findApiUrlByFieldPath(name ?? '', widgets);

  const fetchList = async () => {
    try {
      if (!apiUrl) {
        console.error('API URL not found');
        return;
      }

      setIsFetching(true);
      const response = await axios.get(`${apiUrl}/list`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Accept-Language': getAcceptLanguage(),
        },
      });

      setScheme({ ...response.data });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (apiUrl) {
      fetchList();
    }
  }, [apiUrl]);

  const [isFetching, setIsFetching] = useState<boolean>(false);

  const [filter, setFilter] = useState<string>('');
  const filterDebounced = useDebounce(filter, 1000);
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const [isTableSettingsVisible, setTableSettingsVisible] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const [filterValues, setFilterValues] = useState<Partial<FiltersValues>>({});

  const handleFilter = (values: FiltersValues) => {
    const [startDate, endDate] = JSON.parse(values.period ?? '[]');

    const modified = {
      ...values,
      agreement: values?.agreement === 'all' ? undefined : values?.agreement,
      startDate,
      endDate,
    };
    setFilterValues(modified);
  };

  const handleCreate = () => {
    dispatch(resetFuelFormState());
    navigate(Home + CustomWidget.Home + CustomWidget.NewRequest + `?name=${name}`);
  };

  const revokeItems = async (documentIds: string[]) => {
    setIsRevokeLoading(true);
    if (!documentIds.length) {
      alert.error('No items selected for revocation');
      return;
    }
    try {
      await Promise.all(
        documentIds.map((id) =>
          axios.post(
            `${apiUrl}/${id}/cancel`,
            {},
            {
              headers: {
                Authorization: `Bearer ${getToken()}`,
                'Accept-Language': getAcceptLanguage(),
              },
            },
          ),
        ),
      );
      alert.success('Item revoked');
    } catch (error) {
      console.error('Error revoking items:', error);
      alert.error('Failed to revoke items');
    } finally {
      setIsRevokeLoading(false);
    }
  };

  const handleRevoke = () => {
    revokeItems(selectedRows);
    setSelectedRows([]);
  };

  const onSelectRow = (id: string) => {
    if (selectedRows.some((item) => item === id)) {
      setSelectedRows(selectedRows.filter((item) => item !== id));
      return;
    }

    setSelectedRows([...selectedRows, id]);
  };

  const handleFilterValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFilter(value);
  };

  const columns = useMemo(
    () =>
      scheme?.headers.map((header) => {
        const [key] = Object.keys(header) ?? [];
        return {
          key,
          label: header[key],
        };
      }),

    [scheme],
  );

  const defaultColumns = useMemo(() => {
    return scheme?.headers.map((header) => Object.keys(header)[0]) ?? [];
  }, [scheme?.headers]);

  const handleColumnSelection = (values: any) => {
    const newSelectedColumns = Object.entries(values)
      .filter(([key, value]) => !!value)
      .sort((a, b) => defaultColumns.indexOf(a[0]) - defaultColumns.indexOf(b[0]))
      .map(([key]) => key);

    setSelectedColumns(newSelectedColumns);
    localStorage.setItem('selectedColumns', JSON.stringify(newSelectedColumns));
  };

  const handleResetToDefaults = () => {
    if (defaultColumns.length === 0) {
      return;
    }

    setSelectedColumns(defaultColumns);
    localStorage.setItem('selectedColumns', JSON.stringify(defaultColumns));
  };

  useEffect(() => {
    if (defaultColumns?.length === 0) {
      return;
    }

    const savedColumns = localStorage.getItem('selectedColumns');
    if (savedColumns) {
      setSelectedColumns(JSON.parse(savedColumns));
    } else {
      setSelectedColumns(defaultColumns);
    }
  }, [defaultColumns]);

  if (!scheme) {
    return <Loader size="big" />;
  }

  return (
    <>
      <span className={s['title']}>{scheme?.title}</span>

      <div className={s['container-content']}>
        <div className={s['header']}>
          <div className={s['wrapper-search']}>
            <div className={s['search']}>
              <Input
                allowClear
                value={filter ?? ''}
                prefix={<SearchIcon />}
                placeholder={t('Search...')}
                onChange={handleFilterValue}
                style={{ borderRadius: '0px', height: '40px' }}
              />
            </div>
            <Button
              icon={<FilterIcon />}
              onClick={() => setIsFilter(true)}
              className={cx(s['button'])}
              displayType="outlined-tetriary"
            >
              {t('Filter')}
            </Button>

            <Button
              icon={<Icons.SettingsTable />}
              onClick={() => setTableSettingsVisible(true)}
              className={cx(s['button'], s.buttonSettings)}
              displayType="outlined-tetriary"
            />
          </div>

          <div className={s['wrapper-button']}>
            {scheme?.options?.isTerminable && (
              <Button
                disabled={!selectedRows.length}
                onClick={handleRevoke}
                displayType="outlined-secondary"
                className={cx(s['button'])}
              >
                {t(isRevokeLoading ? 'Loading...' : 'Revoke')}
              </Button>
            )}

            {scheme?.options?.isCreatable && (
              <Button
                icon={<PlusIcon />}
                onClick={handleCreate}
                className={cx(s['button'], s['button-contract'])}
              >
                {t('New request')}
              </Button>
            )}
          </div>
        </div>
        <div className={s['main']}>
          {isFetching ? (
            <Loader size="big" />
          ) : (
            <>
              {scheme?.records?.length === 0 ? (
                <NotFoundContainer>
                  <NotFound
                    notFoundTitle=""
                    hasSearch={false}
                    title="Оперативные заявки"
                    subtitle="отсутствуют"
                  />
                </NotFoundContainer>
              ) : (
                <>
                  <Table
                    selectedRows={selectedRows}
                    onSelectRow={onSelectRow}
                    headers={scheme?.headers ?? []}
                    data={scheme?.records}
                    selectedColumns={selectedColumns}
                  />

                  {/* <Pagination
                    total={scheme?.meta?.total}
                    limit={scheme?.meta?.limit}
                    currentPage={scheme?.meta?.page}
                    onPageChange={(page) => console.log(page)}
                  /> */}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <AsideFilter
        isOpen={isFilter}
        onClose={() => setIsFilter(false)}
        onSubmitForm={handleFilter}
        filters={scheme?.filters ?? []}
      />

      <AsideTableSettings
        isOpen={isTableSettingsVisible}
        onClose={() => setTableSettingsVisible(false)}
        columns={columns ?? []}
        selectedColumns={selectedColumns}
        onSubmitForm={handleColumnSelection}
        onResetToDefaults={handleResetToDefaults}
      />
    </>
  );
}

export default List;
